import React from 'react';
import { Box, Typography } from '@mui/material';
import { DataTableColumn, KeyValuePair } from '@/components/datatable/tableTypes.ts';

function getNestedValue(obj: any, path: string): any {
    if (!path.includes('.')) return obj[path];
    return path.split('.').reduce((value, key) => value?.[key], obj);
}

function compareValues(aVal: any, bVal: any, isAscending: boolean): number {
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
        return isAscending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    const result = aVal > bVal ? 1 : -1;
    return isAscending ? result : -result;
}

export function sortData<T>(data: T[], sortBy: string, sortDirection: 'asc' | 'desc'): T[] {
    if (!sortBy) return data;
    const sorted = [...data];
    sorted.sort((a: any, b: any) => {
        const aVal = getNestedValue(a, sortBy);
        const bVal = getNestedValue(b, sortBy);
        return compareValues(aVal, bVal, sortDirection === 'asc');
    });
    return sorted;
}

export function valueToString(v: any): string {
    if (v == null) return '';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

export function toKeyValuePairs(raw: any): KeyValuePair[] {
    if (raw === null || raw === undefined) return [];

    if (Array.isArray(raw)) {
        if (raw.length === 0) return [];
        if (typeof raw[0] === 'object' && raw[0] !== null && 'value' in raw[0]) {
            return raw as KeyValuePair[];
        }
        return raw.map((item, i) => ({ key: String(i), value: item }));
    }

    if (typeof raw === 'object') {
        return Object.entries(raw).map(([k, v]) => ({
            key: k,
            label: k,
            value: valueToString(v)
        }));
    }

    return [{ key: '0', value: String(raw) }];
}

export function renderMultiLineValue(raw: any): React.ReactNode {
    const pairs = toKeyValuePairs(raw);
    if (pairs.length === 0) return null;
    return (
        <Box className="wcn-table-multiline-container">
            {pairs.map((pair, index) => {
                const hasLabel = pair.label !== undefined;
                return (
                    <Box key={pair.key ?? index} className={`wcn-table-multiline-row ${hasLabel ? '' : 'no-label'}`}>
                        {hasLabel && (
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                className="wcn-table-multiline-label"
                                sx={{ color: 'text.secondary' }}
                            >
                                {pair.label}
                            </Typography>
                        )}
                        <Typography
                            variant="body2"
                            className="wcn-table-multiline-value"
                            sx={{ textAlign: hasLabel ? 'right' : 'left' }}
                        >
                            {pair.value}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

export function renderCellContent<T>(column: DataTableColumn<T>, row: T, rowIndex: number): React.ReactNode {
    if (column.render) return column.render(row, rowIndex);
    if (column.multiLine) {
        const pairs = column.multiLineValue ? column.multiLineValue(row) : (row as any)[column.value];
        return renderMultiLineValue(pairs);
    }
    return (
        <Box sx={{ maxHeight: column.maxHeight ?? 50, overflowY: column.overflowY ?? 'auto' }}>
            {(row as any)[column.value]}
        </Box>
    );
}

export function renderCardMultiLinePairs(pairs: KeyValuePair[]): React.ReactNode {
    return pairs.map((pair, pairIndex) => {
        const hasLabel = !!pair.label;
        return (
            <Box key={pair.key ?? pairIndex} className={`wcn-card-value-multiline-row ${hasLabel ? '' : 'no-label'}`}>
                {pair.label && (
                    <Box className="wcn-card-value-multiline-label">
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', textAlign: 'left', width: '100%' }}
                        >
                            {pair.label}
                        </Typography>
                    </Box>
                )}
                <Box className="wcn-card-value-multiline-value">
                    <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word', textAlign: hasLabel ? 'right' : 'left' }}
                    >
                        {pair.value}
                    </Typography>
                </Box>
            </Box>
        );
    });
}
